import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { RootState, AppDispatch } from '../../redux/store';
import { logout } from '../../redux/authSlice';
import BuylistIcon from '../buylist/BuylistIcon';
import { theme, media, onDark } from '../../styles/theme';
import { brand } from '../../styles/brand';

// No fixed height: like the webshop, the bar is exactly the logo plus its paddings, which works
// out to 153.75px below the breakpoint and 173.75px above it.
//
// The webshop itself has no bottom border, because every section below its header is on the light
// page background. Here the homepage hero is the same dark colour as the header, so without a
// divider the two fuse into one slab — hence the deliberate extra border.
const HeaderContainer = styled.header`
  background-color: ${theme.colors.secondary};
  padding: ${brand.header.paddingMobile};
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid ${onDark.border};
  display: flex;
  align-items: center;

  @media (min-width: ${brand.header.breakpoint}) {
    padding: ${brand.header.paddingDesktop};
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${brand.header.logoPadding};
  text-decoration: none;
  flex-shrink: 0;
`;

// The webshop's logo already contains the wordmark, so there is no separate text alongside it.
// Width is fixed at every viewport, exactly as the webshop sets it; height follows the 288x340
// source and comes out at 118px.
const LogoImage = styled.img`
  width: ${brand.header.logoWidth};
  height: auto;
  display: block;

  /* Smaller below the tablet breakpoint — see the note in brand.header. */
  ${media.tablet} {
    width: ${brand.header.logoWidthSmall};
  }
`;

const MobileControls = styled.div`
  display: none;
  align-items: center;
  gap: 0.75rem;

  ${media.tablet} {
    display: flex;
  }
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${theme.colors.white};
  padding: 0.5rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Nav = styled.nav<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;

  ${media.tablet} {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    flex-direction: column;
    align-items: stretch;
    position: absolute;
    /* Follows the header height, which is now driven by the logo rather than fixed. */
    top: 100%;
    left: 0;
    right: 0;
    background-color: ${theme.colors.secondary};
    border-bottom: ${brand.border.popup} solid ${onDark.border};
    padding: 0.5rem 0;
    z-index: 1000;
  }
`;

const navLinkStyles = css`
  margin-left: 0.25rem;
  padding: 0.5rem 0.75rem;
  color: ${onDark.mutedText};
  text-decoration: none;
  font-weight: ${theme.fontWeights.medium};
  font-size: ${theme.fontSizes.sm};
  letter-spacing: ${brand.typography.navLetterSpacing};
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: ${theme.colors.white};
  }

  ${media.tablet} {
    margin-left: 0;
    padding: 0.75rem 2rem;
    width: 100%;
    border-radius: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);

    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }
  }
`;

const NavLink = styled(Link)`
  ${navLinkStyles}
`;

// External links (e.g. the Shopify webshop) must be plain anchors, not
// react-router Links, so the browser navigates to the absolute URL.
const NavLinkExternal = styled.a`
  ${navLinkStyles}
`;

const NavButton = styled.button`
  margin-left: 0.25rem;
  padding: 0.5rem 0.75rem;
  color: ${onDark.mutedText};
  text-decoration: none;
  font-weight: ${theme.fontWeights.medium};
  font-size: ${theme.fontSizes.sm};
  letter-spacing: ${brand.typography.navLetterSpacing};
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.fast};
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: ${theme.colors.white};
  }

  ${media.tablet} {
    margin-left: 0;
    padding: 0.75rem 2rem;
    width: 100%;
    text-align: left;
    border-radius: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);

    &:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }
  }
`;

const DesktopIconContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0.5rem;

  ${media.tablet} {
    display: none;
  }
`;

const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const location = useLocation();

  // Close menu on route change
  React.useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <HeaderContainer>
      <HeaderContent>
        <LogoContainer to="/">
          <LogoImage src={brand.assets.logo} alt={brand.assets.logoAlt} />
        </LogoContainer>
        <MobileControls>
          <BuylistIcon />
          <HamburgerButton
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Meny"
            aria-expanded={menuOpen}
          >
            {menuOpen ? '✕' : '☰'}
          </HamburgerButton>
        </MobileControls>
        <Nav $isOpen={menuOpen}>
          <NavLinkExternal href="https://tryllepapp.no" target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)}>Kjøp kort</NavLinkExternal>
          <NavLink to="/buylist" onClick={() => setMenuOpen(false)}>Selge kort</NavLink>
          <NavLink to="/csv-opplasting" onClick={() => setMenuOpen(false)}>Last opp kortliste</NavLink>
          <NavLink to="/selge-samling" onClick={() => setMenuOpen(false)}>Selge samling</NavLink>
          <NavLink to="/blogg" onClick={() => setMenuOpen(false)}>Blogg</NavLink>
          {isAuthenticated ? (
            <>
              <NavLink to="/profil" onClick={() => setMenuOpen(false)}>Min profil</NavLink>
              <NavButton onClick={() => { dispatch(logout()); setMenuOpen(false); }}>Logg ut</NavButton>
            </>
          ) : (
            <NavLink to="/login" onClick={() => setMenuOpen(false)}>Logg inn</NavLink>
          )}
          <DesktopIconContainer>
            <BuylistIcon />
          </DesktopIconContainer>
        </Nav>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;
